/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { newNGApi } from '../services/newNGApi';
import { VideoControls } from './VideoControls';
import { LiveIndicator } from './LiveIndicator';
import { HeadlineTicker } from './HeadlineTicker';
import { TheatreSchedulePanel } from './TheatreSchedulePanel';
import { TheatreJobSelector } from './TheatreJobSelector';
import { DebugOverlay } from './DebugOverlay';
import { NewNGJob } from '../types';
import styles from '../NewNG.module.css';

interface VideoPlayerProps {
  jobId: string;
  onHeightChange?: (height: number) => void;
  networkName?: string;
  // When set to 'none', the built-in overlays/controls/ticker are hidden (for embedding in theatre mode)
  controlsMode?: 'full' | 'none';
  // Optional external control of volume/mute from parent UI (e.g., theatre controls)
  externalVolume?: number;
  externalMuted?: boolean;
  // Callback when video playback ends
  onVideoEnd?: () => void;
  // Schedule panel props (for theatre mode)
  scheduleJobs?: NewNGJob[];
  scheduleCurrentJob?: NewNGJob | null;
  // Theatre overlay props (job selector, logo)
  theatreJobs?: NewNGJob[];
  theatreCurrentJob?: NewNGJob | null;
  onJobSelect?: (jobId: string) => void;
  isJobsLoading?: boolean;
  showLogo?: boolean;
  showJobSelector?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
}

const UNMUTE_DELAY_MS = 100;
const DEFAULT_VOLUME = 1.0;

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  jobId,
  onHeightChange,
  networkName = 'FEI',
  controlsMode = 'full',
  externalVolume,
  externalMuted,
  onVideoEnd,
  scheduleJobs,
  scheduleCurrentJob,
  theatreJobs,
  theatreCurrentJob,
  onJobSelect,
  isJobsLoading = false,
  showLogo = false,
  showJobSelector = false,
  showCloseButton = false,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentStateRef = useRef({ volume: DEFAULT_VOLUME, isMuted: true }); // Video starts muted

  const [videoUrl, setVideoUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true); // Start visible, will sync with video state on load
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('landscape');
  const [manualMode, setManualMode] = useState<'portrait' | 'landscape' | null>(null);
  const [volume, setVolume] = useState(DEFAULT_VOLUME);
  const [isMuted, setIsMuted] = useState(true); // Will sync with video element on load
  const [currentJobId, setCurrentJobId] = useState<string>('');
  const [lastProcessedJobId, setLastProcessedJobId] = useState<string>(''); // Track last processed jobId to detect changes
  const [enhancedInfo, setEnhancedInfo] = useState<any>(null);
  const [isVideoPaused, setIsVideoPaused] = useState(true); // Will sync with video element on load
  const [isInteracting, setIsInteracting] = useState(false);
  const [hasUserInteraction, setHasUserInteraction] = useState(false);
  const lastVideoStateRef = useRef<{
    currentTime: number;
    volume: number;
    muted: boolean;
    paused: boolean;
  } | null>(null);


  // ============================================================================
  // CONTROLS VISIBILITY LOGIC DOCUMENTATION
  // ============================================================================
  //
  // The controls (video controls, job selector, close button) visibility is
  // managed by a auto-hide system with the following behavior:
  //
  // STATE VARIABLES:
  // - showControls: Boolean flag controlling CSS visibility of all controls
  // - isVideoPaused: Tracks whether video is currently paused
  // - isMuted: Tracks whether video is currently muted
  // - isInteracting: Short-lived flag (500ms) set during active interaction
  //
  // VISIBILITY RULES:
  //
  // 1. WHEN VIDEO IS PAUSED OR MUTED:
  //    - Controls are ALWAYS shown (auto-hide is disabled)
  //    - Rationale: User needs access to play button and volume controls to unmute/play
  //    - Implementation: Auto-hide timer checks `!isVideoPaused && !isMuted` before hiding
  //
  // 2. WHEN VIDEO IS PLAYING AND UNMUTED:
  //    - Controls auto-hide after 5 seconds of no interaction
  //    - Mouse enter → Controls immediately visible
  //    - Mouse leave → Controls hide after 100ms (if not interacting)
  //    - During interaction (volume change, seeking, etc.) → Controls stay visible
  //
  // 3. INTERACTION HANDLING:
  //    - Any control interaction sets `isInteracting = true` for 500ms
  //    - Prevents auto-hide during active use
  //    - Handled by `resetAutoHideTimer()` callback
  //
  // 4. VIDEO LOAD BEHAVIOR:
  //    - Video's initial state is NON-DETERMINISTIC (depends on browser, device, autoplay policy)
  //    - On loadedMetadata event, we sync React state with actual video element state
  //    - Controls visibility is set based on synced state: visible if paused OR muted
  //    - New videos start paused (line 221) and preserve current showControls state
  //    - State restore logic may override initial state if returning to previous video
  //
  // 5. MUTE STATE HANDLING:
  //    - When muted, controls stay visible so user can unmute
  //    - Auto-hide only triggers when: playing AND unmuted AND no interaction
  //    - Prevents frustrating UX where user can't find unmute button
  //
  // KEY CALLBACKS:
  // - handlePause (line 796): Sets `isVideoPaused=true`, `showControls=true`
  // - handlePlay (line 790): Sets `isVideoPaused=false` (auto-hide can trigger)
  // - resetAutoHideTimer (line 118): Shows controls + sets interaction flag
  //
  // CSS INTEGRATION:
  // - Container gets `newng-controls-visible` class when showControls=true
  // - Job selector and close button have CSS transitions (opacity, transform)
  // - Controls fade in/out smoothly with 0.3s transitions
  //
  // ============================================================================


  // Detect if device is mobile
  const isMobile = useCallback(() => {
    return /Mobi|Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
  }, []);

  // Mobile-friendly video loading - don't try autoplay on mobile
  const shouldAttemptAutoplay = useCallback(() => {
    if (isMobile()) {
      // On mobile, only attempt autoplay if user has explicitly interacted
      return hasUserInteraction && localStorage.getItem('safari-user-interaction') === 'true';
    }
    return true;
  }, [isMobile, hasUserInteraction]);

  // Get current mode (auto-detect or manual override)
  const getCurrentMode = useCallback(() => {
    if (manualMode) return manualMode;
    if (isMobile()) {
      return window.innerHeight > window.innerWidth ? 'portrait' : 'landscape';
    }
    return 'landscape';
  }, [manualMode, isMobile]);

  // Auto-hide controls with longer delay, but keep visible if video is paused or muted
  // See CONTROLS VISIBILITY LOGIC DOCUMENTATION above for complete behavior details
  useEffect(() => {
    if (showControls && !isInteracting && !isVideoPaused && !isMuted) {
      const timer = setTimeout(() => setShowControls(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showControls, isVideoPaused, isMuted, isInteracting]);

  // Reset auto-hide timer when controls are interacted with
  // Sets interaction flag for 500ms to prevent hiding during active use
  const resetAutoHideTimer = useCallback(() => {
    // Ensure controls are visible and reset the timer
    setShowControls(true);
    setIsInteracting(true);

    // Clear interaction flag after a short delay
    setTimeout(() => {
      setIsInteracting(false);
    }, 500);
  }, []);

  // Load job list and get first available job
  const loadJobList = useCallback(async () => {
    try {
      const response = await fetch('/news/api/jobs/');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      if (data.jobs && data.jobs.length > 0) {
        // Use the first job or the provided jobId if it exists
        const selectedJob = jobId || data.jobs[0].job_id;
        setCurrentJobId(selectedJob);

        return selectedJob;
      } else {
        throw new Error('No jobs available');
      }
    } catch (err) {
      console.error('Error loading job list:', err);
      setError('Failed to load job list');
      return null;
    }
  }, [jobId]);

  // Load video with correct file selection
  const loadVideo = useCallback(async (orient: 'portrait' | 'landscape', isJobChange: boolean = false) => {
    try {

      if (isLoadingRef.current) {
        return;
      }
      isLoadingRef.current = true;

      setIsLoading(true);
      setError(null);

      // Get job ID - skip API call for orientation changes
      let selectedJobId;
      if (!isJobChange && currentJobId) {
        // For orientation changes, reuse existing job ID
        selectedJobId = currentJobId;
      } else {
        // For job changes or initial load, fetch from API
        selectedJobId = await loadJobList();
        if (!selectedJobId) {
          throw new Error('No job ID available');
        }
      }



      // Check if this is a job change (different video) or orientation change (same video)
      const isActualJobChange = isJobChange || (selectedJobId !== lastProcessedJobId && lastProcessedJobId !== '');

      let currentState = null;

      if (!isActualJobChange) {
        // Only preserve state for orientation changes (same video)
        const currentVideo = videoRef.current;

        // First try to get state from localStorage (in case component was remounted)
        let savedState = null;
        try {
          const saved = localStorage.getItem('safari-video-state');
          if (saved) {
            const parsed = JSON.parse(saved);
            // Use saved state if it's recent (within 10 seconds) and same job
            if (parsed.jobId === selectedJobId && (Date.now() - parsed.timestamp) < 10000) {
              savedState = parsed;
            }
          }
        } catch (err) {
          // Silent fail - corrupted localStorage data
        }

        currentState = savedState || {
          currentTime: currentVideo?.currentTime || 0,
          volume: currentVideo?.volume || currentStateRef.current.volume,
          muted: currentVideo?.muted ?? currentStateRef.current.isMuted,
          paused: currentVideo?.paused ?? true
        };
      } else {
        // For job changes, start from beginning but preserve volume/mute settings from actual video element
        const currentVideo = videoRef.current;
        const actualVolume = currentVideo?.volume ?? currentStateRef.current.volume;
        const actualMuted = currentVideo?.muted ?? currentStateRef.current.isMuted;

        currentState = {
          currentTime: 0, // Always start from beginning for new videos
          volume: actualVolume,
          muted: actualMuted,
          paused: true // Start paused for new videos
        };
      }

      // Update last processed job ID
      setLastProcessedJobId(selectedJobId);

      // Original overlay.js mapping (keeping as was before):
      // portrait = video_1280x720p.mp4, landscape = video_720x1280p.mp4
      const url = await newNGApi.getVideoUrlForOrientation(selectedJobId, orient);


      // Set the new video URL
      setVideoUrl(url);

      // Store state to restore after video loads
      setPendingStateRestore(currentState);

      // Set paused state immediately to ensure controls visibility logic works correctly
      // New videos always start paused (line 221: paused: true)
      // Setting isVideoPaused=true prevents auto-hide (see CONTROLS VISIBILITY LOGIC)
      // This preserves the current showControls state without forcing visibility
      // The video's onPause event will trigger handlePause which shows controls if needed
      if (currentState.paused) {
        setIsVideoPaused(true);
      }

    } catch (err) {
      console.error('Error loading video:', err);
      setError('Failed to load video');
    } finally {
      setIsLoading(false);
      // REACT FIX: Reset loading flag to allow future loadVideo calls
      isLoadingRef.current = false;
    }
  }, [loadJobList, lastProcessedJobId, currentJobId]);

  // Resize video container to fit video properly
  const resizeVideoContainer = useCallback(() => {
    if (!containerRef.current || !videoRef.current) return;

    const container = containerRef.current;
    const video = videoRef.current;

    if (!video.videoWidth || !video.videoHeight) return;

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const videoAspect = videoWidth / videoHeight;

    // Get the actual container width instead of window width
    const containerWidth = container.parentElement?.clientWidth || window.innerWidth;
    const windowHeight = window.innerHeight;
    const isMobile = window.innerWidth <= 768;

    // Update debug information - target the actual grid container (commented out)
    /*
    const gridContainer = container.parentElement?.parentElement?.parentElement;
    setDebugInfo({
      containerWidth: containerWidth,
      windowWidth: window.innerWidth,
      gridDisplay: gridContainer ?
        getComputedStyle(gridContainer).display : 'N/A',
      isMobile: isMobile,
      videoWidth: videoWidth,
      videoHeight: videoHeight
    });
    */



    // Rule 1 & 2: Never clip, never change aspect ratio, never upsize beyond original
    // Only downsize when needed for container constraints
    let finalVideoWidth = videoWidth;
    let finalVideoHeight = videoHeight;

    if (isMobile) {
      // Mobile: Let CSS Grid handle the width, only manage height
      const maxMobileHeight = windowHeight * 0.8; // 80% of screen height for better visibility

      // Don't set width - let CSS Grid control it
      finalVideoWidth = containerWidth; // Use container width from CSS Grid
      finalVideoHeight = finalVideoWidth / videoAspect;

      // Only constrain height if it exceeds maximum
      if (finalVideoHeight > maxMobileHeight) {
        finalVideoHeight = maxMobileHeight;
        // Don't recalculate width - let CSS Grid handle it
      }
    } else {
      // Desktop: Use container width (which is 70% of parent)
      const maxContainerWidth = containerWidth;

      if (finalVideoWidth > maxContainerWidth) {
        finalVideoWidth = maxContainerWidth;
        finalVideoHeight = finalVideoWidth / videoAspect;
      }
    }

    // Rule 3: Let CSS Grid handle width, only set height
    if (isMobile) {
      // Mobile: Don't override CSS Grid width, only set height
      container.style.width = '100%'; // Let CSS Grid control width
      container.style.height = `${finalVideoHeight}px`;
    } else {
      // Desktop: Set both width and height
      container.style.width = `${finalVideoWidth}px`;
      container.style.height = `${finalVideoHeight}px`;
    }
    container.style.padding = '0';
    container.style.margin = '0';
    container.style.overflow = 'visible';

    // Rule 4: Video element = container size
    video.style.width = '100%';
    video.style.height = '100%';

    // Notify parent component of height change
    if (onHeightChange) {
      onHeightChange(finalVideoHeight);
    }


  }, [onHeightChange]);



  // Handle window resize - FIXED: Remove duplicate orientation handling
  const handleResize = useCallback(() => {
    // Only handle resize for container sizing, not orientation changes
    resizeVideoContainer();
    // Orientation changes are handled by orientationchange event only
  }, [resizeVideoContainer]);

  // Set up event listeners - DISABLED: Auto orientation detection removed
  useEffect(() => {
    // REMOVED: Auto orientation detection
    // window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleResize);

    return () => {
      // REMOVED: Auto orientation detection
      // window.removeEventListener('orientationchange', handleOrientationChange);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]); // Removed handleOrientationChange dependency



  // State to track if we need to restore video state after load
  const [pendingStateRestore, setPendingStateRestore] = useState<{
    currentTime: number;
    volume: number;
    muted: boolean;
    paused: boolean;
  } | null>(null);

  // REACT FIX: Prevent race conditions with loading state
  const isLoadingRef = useRef(false);

  // Function to restore video state with robust validation
  const restoreVideoState = useCallback((video: HTMLVideoElement, state: any) => {

    // Validate video is ready
    if (!video.duration || video.duration <= 0) {
      return false;
    }

    // Restore timestamp (but clamp to video duration)
    if (state.currentTime > 0) {
      const targetTime = Math.min(state.currentTime, video.duration);
      video.currentTime = targetTime;
    }

    // Restore volume and mute state
    video.volume = state.volume;
    video.muted = state.muted;

    // DON'T set isVideoPaused here - let native onPlay/onPause events handle it
    // Setting it here causes race conditions with actual video playback state

    // REACT FIX: Handle autoplay separately - this function only handles position/volume restoration
    if (!state.paused) {

      // REACT FIX: Re-check localStorage for user interaction before autoplay
      let userInteractionPermission = hasUserInteraction;
      try {
        const saved = localStorage.getItem('safari-user-interaction');
        if (saved === 'true' && !hasUserInteraction) {
          userInteractionPermission = true;
          setHasUserInteraction(true);
        }
      } catch (err) {
        // Silent fail
      }

      // Try autoplay immediately if video is well-loaded (mobile-friendly)
      if (userInteractionPermission && video.readyState >= 3 && shouldAttemptAutoplay()) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            setPendingStateRestore(null);
          }).catch(err => {
            console.log('Autoplay failed:', err.message);
            setShowControls(true);
            // DON'T set isVideoPaused here - onPause event will handle it
          });
        }
      } else {
        setShowControls(true);
        // DON'T set isVideoPaused here - onPause event will handle it
      }
    }
    // REACT FIX: Always return true for position/volume restoration success
    // Autoplay is handled separately and doesn't affect this return value
    return true;
  }, [hasUserInteraction, shouldAttemptAutoplay]);

  // Attempt to restore state when video is ready
  const attemptStateRestore = useCallback(() => {

    if (pendingStateRestore && videoRef.current) {
      const video = videoRef.current;

      // Check if video is ready for state restoration (Safari-friendly)
      if (video.duration && video.duration > 0 && video.readyState >= 1) {
        const success = restoreVideoState(video, pendingStateRestore);
        if (success) {
          // REACT FIX: Clear pending state only if video was paused (no autoplay needed)
          // If video was playing, keep pendingStateRestore for video event autoplay retries
          if (pendingStateRestore.paused) {
            setPendingStateRestore(null);
          }
        }
      }
    }
  }, [pendingStateRestore, restoreVideoState]);

  // Save video state continuously to survive component remounts
  useEffect(() => {
    const saveVideoState = () => {
      if (videoRef.current) {
        const currentState = {
          currentTime: videoRef.current.currentTime || 0,
          volume: videoRef.current.volume || 0.7,
          muted: videoRef.current.muted || false,
          paused: videoRef.current.paused ?? true
        };
        lastVideoStateRef.current = currentState;

        // Save to localStorage to survive orientation-triggered remounts
        try {
          localStorage.setItem('safari-video-state', JSON.stringify({
            ...currentState,
            timestamp: Date.now(),
            jobId: currentJobId,
            orientation: orientation
          }));
        } catch (err) {
          // Silent fail - localStorage might be full
        }
      }
    };

    // Save state every 5 seconds when video is playing
    const interval = setInterval(() => {
      // Only save if video is actually playing to avoid log spam
      if (videoRef.current && !videoRef.current.paused) {
        saveVideoState();
      }
    }, 5000);

    // Save state on visibility change (orientation changes often trigger this)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveVideoState();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      saveVideoState(); // Save one final time
    };
  }, [currentJobId, orientation]);

  // Restore hasUserInteraction state from localStorage on mount
  useEffect(() => {
    const storedInteraction = localStorage.getItem('safari-user-interaction');
    if (storedInteraction === 'true') {
      setHasUserInteraction(true);
    }
  }, []);

  useEffect(() => {
    const handleVideoClick = () => {
      setHasUserInteraction(true);
      localStorage.setItem('safari-user-interaction', 'true');
    };

    const video = videoRef.current;
    if (video) {
      video.addEventListener('click', handleVideoClick);
      return () => video.removeEventListener('click', handleVideoClick);
    }
  }, [videoUrl]); // Re-attach when video source changes

  // Load initial video - FIXED: Prevent duplicate calls on loadVideo dependency changes
  useEffect(() => {
    const initialMode = getCurrentMode();
    setOrientation(initialMode);
    // Update ref with initial state
    currentStateRef.current = { volume, isMuted };
    loadVideo(initialMode, false); // false indicates initial load, start from beginning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Load enhanced info when jobId changes
  useEffect(() => {
    const loadEnhancedInfo = async () => {
      if (!jobId) {
        setEnhancedInfo(null);
        return;
      }

      try {
        const enhancedInfo = await newNGApi.getEnhancedJobInfo(jobId);
        setEnhancedInfo(enhancedInfo);
      } catch (error) {
        console.error('Failed to load enhanced info:', error);
        setEnhancedInfo(null);
      }
    };

    loadEnhancedInfo();
  }, [jobId]);

  // Handle jobId changes - reload video from beginning
  useEffect(() => {
    if (jobId && jobId !== lastProcessedJobId && lastProcessedJobId !== '') {
      const currentMode = getCurrentMode();
      setOrientation(currentMode);
      loadVideo(currentMode, true);

      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.focus({ preventScroll: true });
        }
      }, 100); // Small delay to ensure video is loading
    }
  }, [jobId, lastProcessedJobId, getCurrentMode, loadVideo]);

  // Update ref when volume/mute state changes (without triggering video reloads)
  useEffect(() => {
    currentStateRef.current = { volume, isMuted };

    // Also sync with the actual video element if it exists
    if (videoRef.current) {
      videoRef.current.volume = volume;
      videoRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // Sync external volume/mute from parent, if provided
  useEffect(() => {
    if (typeof externalVolume === 'number') {
      setVolume(externalVolume);
      if (videoRef.current) {
        videoRef.current.volume = externalVolume;
      }
    }
  }, [externalVolume]);

  useEffect(() => {
    if (typeof externalMuted === 'boolean') {
      setIsMuted(externalMuted);
      if (videoRef.current) {
        videoRef.current.muted = externalMuted;
      }
    }
  }, [externalMuted]);

  // Fallback: Attempt state restoration when video URL changes with retry logic
  useEffect(() => {
    if (videoUrl && pendingStateRestore) {
      let retryCount = 0;
      const maxRetries = 5;

      const attemptWithRetry = () => {
        if (retryCount >= maxRetries) {
          setPendingStateRestore(null);
          return;
        }

        retryCount++;
        attemptStateRestore();

        // If still pending, schedule next retry
        if (pendingStateRestore) {
          setTimeout(attemptWithRetry, 200 * retryCount); // Exponential backoff
        }
      };

      // Initial attempt after short delay
      const initialTimer = setTimeout(attemptWithRetry, 100);

      return () => clearTimeout(initialTimer);
    }
  }, [videoUrl, pendingStateRestore, attemptStateRestore]);

  const handleCanPlay = useCallback(() => {
    attemptStateRestore();

    // REACT FIX: Try autoplay on canplay if we have pending restore and user interaction
    if (pendingStateRestore && !pendingStateRestore.paused && videoRef.current) {
      // REACT FIX: Re-check localStorage for user interaction
      let userInteractionPermission = hasUserInteraction;
      try {
        const saved = localStorage.getItem('safari-user-interaction');
        if (saved === 'true') {
          userInteractionPermission = true;
          if (!hasUserInteraction) {
            setHasUserInteraction(true);
          }
        }
      } catch (err) {
        // Silent fail
      }

      if (userInteractionPermission && shouldAttemptAutoplay()) {
        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.then(() => {
            // REACT FIX: Clear pending state only when autoplay succeeds
            setPendingStateRestore(null);
          }).catch(err => {
            console.log('Autoplay failed:', err.message);
            setShowControls(true);
            // DON'T set isVideoPaused here - onPause event will handle it
          });
        }
      }
    }
  }, [attemptStateRestore, pendingStateRestore, hasUserInteraction]);

  const handleLoadedMetadata = useCallback(() => {
    resizeVideoContainer();

    // Sync React state with actual video element state (non-deterministic due to browser policies)
    // Note: At loadedMetadata, video hasn't started playing yet even with autoPlay
    // We sync muted/volume here, but paused state will be synced via onPlay/onPause events
    if (videoRef.current) {
      const video = videoRef.current;
      const actualMuted = video.muted;
      const actualVolume = video.volume;

      // Update muted and volume state immediately
      setIsMuted(actualMuted);
      setVolume(actualVolume);

      // Update ref
      currentStateRef.current = { volume: actualVolume, isMuted: actualMuted };

      // Don't set paused state here - let onPlay/onPause events handle it
      // Set controls visible if muted (paused state will be handled by play/pause events)
      if (actualMuted) {
        setShowControls(true);
      }
    }

    attemptStateRestore();
  }, [resizeVideoContainer, attemptStateRestore]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleProgress = useCallback(() => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1);
      const duration = videoRef.current.duration;
      const bufferedPercent = (bufferedEnd / duration) * 100;

      // Try state restoration when we have sufficient buffering
      if (bufferedPercent > 10) {
        attemptStateRestore();
      }
    }
  }, [attemptStateRestore]);

  const handleWaiting = useCallback(() => {
  }, []);

  const attemptAutoplayWithUnmute = useCallback((video: HTMLVideoElement) => {
    const isDesktop = !isMobile();
    const shouldUnmuteAfterPlay = isDesktop && video.muted;

    const playPromise = video.play();
    if (!playPromise) {
      return;
    }

    playPromise
      .then(() => {
        console.log('Autoplay started successfully');

        if (shouldUnmuteAfterPlay) {
          setTimeout(() => {
            video.muted = false;
            video.volume = DEFAULT_VOLUME;
            setIsMuted(false);
            setVolume(DEFAULT_VOLUME);
            console.log('Desktop: Unmuted to full volume');
          }, UNMUTE_DELAY_MS);
        }

        if (pendingStateRestore) {
          setPendingStateRestore(null);
        }
      })
      .catch(err => {
        console.log('Autoplay blocked:', err.message);
        setShowControls(true);
        // DON'T set isVideoPaused here - onPause event will handle it
      });
  }, [isMobile, pendingStateRestore]);

  const handleCanPlayThrough = useCallback(() => {
    attemptStateRestore();

    if (!videoRef.current || !videoRef.current.paused) {
      return;
    }

    const video = videoRef.current;
    const isDesktop = !isMobile();
    const canAttemptAutoplay = isDesktop || shouldAttemptAutoplay();

    if (canAttemptAutoplay) {
      attemptAutoplayWithUnmute(video);
    }

    if (!pendingStateRestore || pendingStateRestore.paused || !videoRef.current) {
      return;
    }

    let userInteractionPermission = hasUserInteraction;
    try {
      const saved = localStorage.getItem('safari-user-interaction');
      if (saved === 'true') {
        userInteractionPermission = true;
        if (!hasUserInteraction) {
          setHasUserInteraction(true);
        }
      }
    } catch (err) {
      // Silent fail on localStorage access
    }

    if (userInteractionPermission && shouldAttemptAutoplay()) {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setPendingStateRestore(null);
          })
          .catch(err => {
            console.log('Autoplay failed:', err.message);
            setShowControls(true);
            // DON'T set isVideoPaused here - onPause event will handle it
          });
      }
    }
  }, [attemptStateRestore, attemptAutoplayWithUnmute, pendingStateRestore, hasUserInteraction, shouldAttemptAutoplay, isMobile]);

  // Handle volume changes from video element (e.g., browser controls)
  const handleVolumeChangeFromVideo = useCallback(() => {
    if (videoRef.current) {
      const video = videoRef.current;
      const newVolume = video.volume;
      const newMuted = video.muted;

      // Update state to match video element
      setVolume(newVolume);
      setIsMuted(newMuted);

      // Update ref
      currentStateRef.current = { volume: newVolume, isMuted: newMuted };
    }
  }, []);

  // Handle play event
  const handlePlay = useCallback(() => {
    setIsVideoPaused(false); // Enables auto-hide behavior (see CONTROLS VISIBILITY LOGIC)
    setHasUserInteraction(true);
    localStorage.setItem('safari-user-interaction', 'true');
  }, []);

  const handlePause = useCallback(() => {
    setIsVideoPaused(true); // Disables auto-hide behavior
    setShowControls(true);   // Always show controls when paused (user needs play button)
  }, []);

  const togglePlay = useCallback(() => {
    resetAutoHideTimer();
    setHasUserInteraction(true);
    localStorage.setItem('safari-user-interaction', 'true');
    if (videoRef.current) {
      try {
        if (videoRef.current.paused) {
          // Handle mobile autoplay restrictions
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            playPromise.then(() => {
              // Playback started successfully - onPlay event will set isVideoPaused=false
            }).catch((error) => {
              // Handle autoplay failure (common on mobile)
              console.log('Autoplay prevented:', error.message);
              setShowControls(true);
              // DON'T set isVideoPaused - onPause event will handle it

              // Show user-friendly message for mobile
              if (isMobile()) {
                // On mobile, show a message that user needs to tap play
                setError('Tap the play button to start video (mobile autoplay restricted)');
              }
            });
          }
        } else {
          videoRef.current.pause();
          // DON'T set isVideoPaused - onPause event will handle it
        }
      } catch (error) {
        // Handle any unexpected errors during play/pause
        console.log('Video operation failed:', error);
        setShowControls(true);
        // DON'T set isVideoPaused - video state events will handle it

        if (isMobile()) {
          setError('Video operation failed. Please try again.');
        }
      }
    }
  }, [resetAutoHideTimer, isMobile]);

  const toggleMute = useCallback(() => {
    resetAutoHideTimer();
    setHasUserInteraction(true);
    localStorage.setItem('safari-user-interaction', 'true');
    const newMutedState = !isMuted;
    setIsMuted(newMutedState);
    currentStateRef.current.isMuted = newMutedState;
    if (videoRef.current) {
      videoRef.current.muted = newMutedState;
    }
  }, [isMuted, resetAutoHideTimer]);

  const handleVolumeChange = useCallback((newVolume: number) => {
    resetAutoHideTimer();
    setHasUserInteraction(true);
    localStorage.setItem('safari-user-interaction', 'true');
    setVolume(newVolume);
    currentStateRef.current.volume = newVolume;
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      if (newVolume > 0 && isMuted) {
        setIsMuted(false);
        currentStateRef.current.isMuted = false;
        videoRef.current.muted = false;
      }
    }
  }, [isMuted, resetAutoHideTimer]);

  const toggleOrientation = useCallback(() => {
    resetAutoHideTimer();
    setHasUserInteraction(true);
    localStorage.setItem('safari-user-interaction', 'true');
    const newMode = orientation === 'portrait' ? 'landscape' : 'portrait';
    setManualMode(newMode);
    setOrientation(newMode);
    loadVideo(newMode, false);
  }, [orientation, loadVideo, resetAutoHideTimer]);

  const resetOrientation = useCallback(() => {
    resetAutoHideTimer();
    setHasUserInteraction(true);
    localStorage.setItem('safari-user-interaction', 'true');
    setManualMode(null);
    // MANUAL DETECTION: Reset to actual device orientation
    const currentMode = getCurrentMode();
    setOrientation(currentMode);
    loadVideo(currentMode, false);
  }, [getCurrentMode, loadVideo, resetAutoHideTimer]);

  if (isLoading) {
    return (
      <div className={styles['newng-video-section']}>
        <div className={styles['newng-loading']}>
          <div className={styles['newng-spinner']}></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles['newng-video-section']}>
        <div className={styles['newng-error']}>
          <div className={styles['newng-error-icon']}>⚠️</div>
          <div className={styles['newng-error-message']}>Video Error</div>
          <div className={styles['newng-error-details']}>{error}</div>
          {isMobile() && (
            <div className={styles['newng-mobile-help']}>
              <p>On mobile devices, tap the play button to start video playback.</p>
              <button
                className={styles['newng-retry-button']}
                onClick={() => {
                  setError(null);
                  setShowControls(true);
                }}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles['newng-video-section']}>
      <DebugOverlay enabled={false} />
      <div
        ref={containerRef}
        className={`${styles['newng-video-container']} ${showControls ? styles['newng-controls-visible'] : ''}`}
        tabIndex={0}
        aria-label="Video player"
        onMouseEnter={() => setShowControls(true)} // Always show on hover
        onClick={() => {
          setHasUserInteraction(true);
          localStorage.setItem('safari-user-interaction', 'true');
        }}
        onMouseLeave={() => {
          // Only hide controls if not currently interacting, video is not paused, and video is not muted
          // See CONTROLS VISIBILITY LOGIC DOCUMENTATION for complete behavior
          if (!isInteracting && !isVideoPaused && !isMuted) {
            setTimeout(() => setShowControls(false), 100);
          }
        }}
        onTouchStart={() => {
          setShowControls(true);
          setHasUserInteraction(true);
          localStorage.setItem('safari-user-interaction', 'true');
        }}
        onTouchEnd={() => {
          // On mobile, don't immediately hide controls on touch end
          // Let the auto-hide timer handle it
        }}
        onTouchMove={() => {
          // Prevent scrolling when touching video controls
        }}
      >
        <div className={styles['newng-video-content']}>
          <video
            ref={videoRef}
            className={`${styles['newng-video-player']} ${orientation === 'portrait' ? styles['newng-video-portrait'] : styles['newng-video-landscape']}`}
            src={videoUrl || undefined}
            onLoadedMetadata={handleLoadedMetadata}
            onCanPlay={handleCanPlay}
            onCanPlayThrough={handleCanPlayThrough}
            onProgress={handleProgress}
            onWaiting={handleWaiting}
            onVolumeChange={handleVolumeChangeFromVideo}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={() => {
              console.log('Video playback ended');
              if (onVideoEnd) {
                onVideoEnd();
              }
            }}
            onClick={() => {
              setHasUserInteraction(true);
              localStorage.setItem('safari-user-interaction', 'true');
            }}
            playsInline
            preload="metadata"
            muted={true}
            autoPlay={true}
          />

          {/* Top-right: Logo and Live indicator side by side */}
          {controlsMode === 'full' && (showLogo || true) && (
            <div className={styles['newng-theatre-logo-container']}>
              {showLogo && (
                <img
                  src="/news/demo/public/assets/logo.png"
                  alt="News Logo"
                  className={styles['newng-theatre-logo-image']}
                />
              )}
              <LiveIndicator textOnly={true} />
            </div>
          )}

          {/* Theatre top overlays (close button and job selector side by side) - auto-hide with controls */}
          {/* These elements are always rendered but hidden via CSS when showControls=false */}
          {/* See .newng-theatre-top-controls in CSS for transition behavior */}
          {controlsMode === 'full' && (showCloseButton || showJobSelector) && (
            <div className={styles['newng-theatre-top-controls']}>
              {showCloseButton && (
                <button
                  className={styles['newng-theatre-back-button']}
                  onClick={onClose}
                  onMouseEnter={resetAutoHideTimer} // Keep controls visible while hovering
                  title="Close theatre mode"
                >
                  <span className={styles['newng-theatre-back-icon']}>✕</span>
                </button>
              )}

              {showJobSelector && theatreJobs && (
                <div onMouseEnter={resetAutoHideTimer}> {/* Keep controls visible while hovering */}
                  <TheatreJobSelector
                    jobs={theatreJobs}
                    currentJob={theatreCurrentJob || null}
                    onJobSelect={(job) => onJobSelect?.(job.job_id)}
                    isLoading={isJobsLoading}
                  />
                </div>
              )}
            </div>
          )}

          {/* Bottom overlay bars */}
          {controlsMode === 'full' && (
            <div className={styles['newng-video-overlay-bars']}>
              {/* Row 1: Controls (left) + Schedule Panel (right) */}
              <div className={styles['newng-overlay-top-row']}>
                {/* Video controls overlay - left column, align bottom */}
                <VideoControls
                  showControls={showControls}
                  onTogglePlay={togglePlay}
                  onToggleMute={toggleMute}
                  onVolumeChange={handleVolumeChange}
                  onToggleOrientation={toggleOrientation}
                  onResetOrientation={resetOrientation}
                  isMuted={isMuted}
                  volume={volume}
                  orientation={orientation}
                  manualMode={manualMode}
                  isPaused={isVideoPaused}
                  currentJobId={jobId}
                />

                {/* Schedule Panel - right column, independent height */}
                {scheduleJobs && scheduleCurrentJob !== undefined && (
                  <TheatreSchedulePanel
                    jobs={scheduleJobs}
                    currentJob={scheduleCurrentJob}
                    maxUpcomingItems={5}
                  />
                )}
              </div>

              {/* Row 2 & 3: Headline ticker (red bar + headlines) */}
              <HeadlineTicker jobId={currentJobId} networkName={networkName} enhancedInfo={enhancedInfo} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
